/**
 * ActivityDetailSheet — full forensic detail for a single Activity Event.
 *
 * Shows everything an auditor needs to reconstruct what happened:
 *   - Hash-chain proof (prev hash, this hash, sequence number)
 *   - Parent event + sibling events in the same session (full timeline)
 *   - Truncated input/output with "view full" toggles
 *   - Framework controls touched
 *   - Classification
 *   - All metadata as navigable JSON
 *   - Cross-module links (user → profile, agent → /agents, workflow → /automate)
 *   - "Verify hash chain from here" action
 *   - "Export this event" + "Export session" actions
 */

import { useMemo, useState, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import {
  X, Hash, Shield, User, Bot, Workflow as WorkflowIcon, Clock, Copy,
  FileText, Download, ExternalLink, CheckCircle2, AlertTriangle,
  ArrowUp, ArrowDown, ChevronRight, Network, Code2, Activity,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  OUTCOME_STYLES, SEVERITY_STYLES, CATEGORY_STYLES, CLASSIFICATION_STYLES,
  type ActivityEvent,
} from '@/lib/mock-trust-activity';
import { triggerDownload } from '@/lib/trust-downloads';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import { apiClient } from '@/lib/api-client';

// Neutral fallback so an event whose category/outcome/severity/classification the
// server emits but this fixed style map doesn't know (the adapters cast
// `as EventCategory` etc. with no validation) degrades to a plain badge instead of
// crashing this sheet via `.color` on undefined. Mirrors routes/trust/activity.tsx.
const FALLBACK_STYLE = { label: '', color: 'text-muted-foreground border-muted-foreground/30' } as const;

/** Result of a real backend audit-chain verification walk. */
interface ChainVerifyResult {
  passed: boolean;
  recordsChecked: number;
  firstFailureAt: number | null;
  firstFailureReason: string | null;
  from: number;
  to: number;
}

export interface ActivityDetailSheetProps {
  event: ActivityEvent | null;
  onClose: () => void;
  allEvents: ActivityEvent[];
  /** Optional callback to switch the displayed event without closing the sheet. */
  onEventChange?: (next: ActivityEvent) => void;
}

type DetailTab = 'overview' | 'session' | 'payload' | 'metadata' | 'chain';

export function ActivityDetailSheet({ event, onClose, allEvents, onEventChange }: ActivityDetailSheetProps) {
  const [tab, setTab] = useState<DetailTab>('overview');

  // ── Related events in the same session ────────────────────────────────────
  const sessionEvents = useMemo(() => {
    if (!event) return [];
    const rootId = event.parentEventId ?? event.id;
    // Gather all events that share conversationId OR parent chain to root
    const conv = event.conversationId;
    return allEvents.filter(e =>
      (conv && e.conversationId === conv)
      || e.id === rootId
      || e.parentEventId === rootId,
    ).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  }, [event, allEvents]);

  // Parent + siblings
  const parentEvent = useMemo(() => {
    if (!event?.parentEventId) return null;
    return allEvents.find(e => e.id === event.parentEventId) ?? null;
  }, [event, allEvents]);

  const childEvents = useMemo(() => {
    if (!event) return [];
    return allEvents.filter(e => e.parentEventId === event.id);
  }, [event, allEvents]);

  // Real audit-chain verification — wired to the backend verify endpoint
  // (POST /governance/audit/chain/verify). We do NOT assert integrity until a
  // verification actually runs and returns a result (truth over speed): the
  // panel stays neutral until the user clicks Verify, then reflects the real
  // pass/fail. The previous implementation hard-coded `valid: true` + a toast
  // claiming "no tampering detected" without ever calling the server.
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<ChainVerifyResult | null>(null);
  // Clear any prior result when the inspected event changes.
  useEffect(() => {
    setVerifyResult(null);
  }, [event?.id]);

  // Entity lookups (users/agents/workflows). This hook MUST be called
  // unconditionally, BEFORE the `if (!event) return null` guard below. When it
  // sat after the early return, the hook count differed between the closed
  // (event=null → fewer hooks) and open (event set → this hook runs) renders,
  // throwing React #310 "rendered more hooks than during the previous render"
  // and crashing the whole Activity Trail via the error boundary.
  const entities = useTrustEntities();

  if (!event) return null;

  // ── Derived display data ─────────────────────────────────────────────────
  const realUser = entities.lookup.user(event.userId);
  const realAgent = entities.lookup.agent(event.agentId);
  const realWorkflow = entities.lookup.workflow(event.workflowId);
  // Adapt lookup result into the legacy `user`/`agent`/`workflow` VM
  // shape used by the existing JSX. Missing entries fall through to
  // `null`; render code already handles that.
  const user = realUser
    ? {
        id: realUser.id,
        name: realUser.name,
        email: realUser.email,
        role: realUser.role,
        clearance: '—',
        isExternal: false,
        avatarColor: 'blue',
      }
    : event.userId
    ? {
        id: event.userId,
        name: `user:${shortId(event.userId)}`,
        email: '',
        role: '',
        clearance: '—',
        isExternal: false,
        avatarColor: 'zinc',
      }
    : null;
  const agent = realAgent
    ? {
        id: realAgent.id,
        name: realAgent.name,
        type: realAgent.type,
        linkedAgentId: realAgent.linkedAgentId,
        iconColor: realAgent.iconColor,
      }
    : event.agentId
    ? {
        id: event.agentId,
        name: `agent:${shortId(event.agentId)}`,
        type: 'unknown',
        linkedAgentId: null,
        iconColor: 'zinc',
      }
    : null;
  const workflow = realWorkflow
    ? {
        id: realWorkflow.id,
        name: realWorkflow.name,
        linkedWorkflowId: realWorkflow.linkedWorkflowId,
        iconColor: realWorkflow.iconColor,
      }
    : event.workflowId
    ? {
        id: event.workflowId,
        name: `workflow:${shortId(event.workflowId)}`,
        linkedWorkflowId: null,
        iconColor: 'zinc',
      }
    : null;

  const oc = OUTCOME_STYLES[event.outcome] ?? { ...FALLBACK_STYLE, label: event.outcome };
  const sev = SEVERITY_STYLES[event.severity] ?? { ...FALLBACK_STYLE, label: event.severity };
  const cat = CATEGORY_STYLES[event.category] ?? { ...FALLBACK_STYLE, label: event.category };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleExportEvent = () => {
    triggerDownload({
      filename: `event-${event.sequenceNumber}.json`,
      content: JSON.stringify(event, null, 2),
      mimeType: 'application/json',
    });
    toast.success('Event exported');
  };

  const handleExportSession = () => {
    triggerDownload({
      filename: `session-${event.conversationId ?? event.id}.json`,
      content: JSON.stringify(sessionEvents, null, 2),
      mimeType: 'application/json',
    });
    toast.success(`Session exported (${sessionEvents.length} events)`);
  };

  const handleVerifyChain = async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      const res = await apiClient.post<{
        passed: boolean;
        recordsChecked: number;
        firstFailureAt: number | null;
        firstFailureReason: string | null;
        verifiedRange: { from: number; to: number };
      }>('/governance/audit/chain/verify', {
        fromSequence: Math.max(1, event.sequenceNumber),
      });
      setVerifyResult({
        passed: res.passed,
        recordsChecked: res.recordsChecked,
        firstFailureAt: res.firstFailureAt,
        firstFailureReason: res.firstFailureReason,
        from: res.verifiedRange.from,
        to: res.verifiedRange.to,
      });
      if (res.passed) {
        toast.success('Hash chain verified', {
          description: `Checked ${res.recordsChecked} record(s) (#${res.verifiedRange.from}–#${res.verifiedRange.to}) on the server. All hashes match — no tampering detected.`,
        });
      } else {
        toast.error('Chain verification FAILED', {
          description:
            res.firstFailureReason ??
            `Tampering detected at sequence #${res.firstFailureAt ?? '?'}.`,
        });
      }
    } catch (err) {
      toast.error('Could not verify chain', {
        description:
          err instanceof Error ? err.message : 'The verification request failed.',
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Sheet open={!!event} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl w-full overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="p-6 pb-4 border-b">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <Badge variant="outline" className={cn('text-[10px]', cat.color)}>{cat.label}</Badge>
                <Badge variant="outline" className={cn('text-[10px]', oc.color)}>{oc.label}</Badge>
                <Badge variant="outline" className={cn('text-[10px]', sev.color)}>{sev.label}</Badge>
                <span className="text-[10px] text-muted-foreground font-mono">#{event.sequenceNumber}</span>
              </div>
              <SheetTitle className="text-sm">{event.summary}</SheetTitle>
              <SheetDescription className="text-xs mt-1">
                {event.type.replace(/_/g, ' ')} · {new Date(event.timestamp).toLocaleString()}
              </SheetDescription>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Close" title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Actor row */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {user && (
              <div className="flex items-center gap-1.5">
                <User className={cn('h-3.5 w-3.5', `text-${user.avatarColor}-600 dark:text-${user.avatarColor}-400`)} />
                <span className="font-medium text-foreground">{user.name}</span>
                <span>· {user.role}</span>
              </div>
            )}
            {agent && (
              <>
                {user && <ChevronRight className="h-3 w-3" />}
                <div className="flex items-center gap-1.5">
                  <Bot className={cn('h-3.5 w-3.5', `text-${agent.iconColor}-600 dark:text-${agent.iconColor}-400`)} />
                  {agent.linkedAgentId ? (
                    <Link
                      to={'/agents/$agentId' as any}
                      params={{ agentId: agent.linkedAgentId } as any}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {agent.name}
                    </Link>
                  ) : (
                    <span className="font-medium text-foreground">{agent.name}</span>
                  )}
                </div>
              </>
            )}
            {workflow && (
              <>
                {(user || agent) && <ChevronRight className="h-3 w-3" />}
                <div className="flex items-center gap-1.5">
                  <WorkflowIcon className={cn('h-3.5 w-3.5', `text-${workflow.iconColor}-600 dark:text-${workflow.iconColor}-400`)} />
                  {workflow.linkedWorkflowId ? (
                    <Link
                      to={'/automate/workflows/$id' as any}
                      params={{ id: workflow.linkedWorkflowId } as any}
                      className="font-medium text-foreground hover:text-primary"
                    >
                      {workflow.name}
                    </Link>
                  ) : (
                    <span className="font-medium text-foreground">{workflow.name}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as DetailTab)} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-3 border-b">
            <TabsList className="h-9">
              <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
              <TabsTrigger value="session" className="text-xs">
                Session Timeline
                <span className="text-[9px] text-muted-foreground ml-1">({sessionEvents.length})</span>
              </TabsTrigger>
              <TabsTrigger value="payload" className="text-xs">Payload</TabsTrigger>
              <TabsTrigger value="metadata" className="text-xs">Metadata</TabsTrigger>
              <TabsTrigger value="chain" className="text-xs">
                <Hash className="h-3 w-3 mr-1" /> Hash Chain
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* ─── Overview ─────────────────────────────────────────────── */}
            <TabsContent value="overview" className="m-0 p-6 space-y-5">
              <SectionCard title="Event">
                <Row label="Type"        value={<code className="text-[11px]">{event.type}</code>} />
                <Row label="Category"    value={<Badge variant="outline" className={cn('text-[10px]', cat.color)}>{cat.label}</Badge>} />
                <Row label="Outcome"     value={<Badge variant="outline" className={cn('text-[10px]', oc.color)}>{oc.label}</Badge>} />
                <Row label="Severity"    value={<Badge variant="outline" className={cn('text-[10px]', sev.color)}>{sev.label}</Badge>} />
                <Row label="Timestamp"   value={<span className="text-[11px]">{new Date(event.timestamp).toLocaleString()} <span className="text-muted-foreground">({relativeTime(event.timestamp)})</span></span>} />
                {event.durationMs !== null && <Row label="Duration" value={<span className="text-[11px]">{event.durationMs.toLocaleString()} ms</span>} />}
                <Row label="Source system" value={<code className="text-[11px]">{event.sourceSystem ?? '—'}</code>} />
              </SectionCard>

              <SectionCard title="Actor & Target">
                <Row label="Actor type"  value={<span className="text-[11px] capitalize">{event.actorType}</span>} />
                {user && <Row label="User" value={<span className="text-[11px]">{user.name} <span className="text-muted-foreground">({user.email}) · {user.role} · clearance={user.clearance}{user.isExternal ? ' · external' : ''}</span></span>} />}
                {agent && <Row label="Agent" value={<span className="text-[11px]">{agent.name} <span className="text-muted-foreground">({agent.type})</span></span>} />}
                {workflow && <Row label="Workflow" value={<span className="text-[11px]">{workflow.name}</span>} />}
                {event.targetType && <Row label="Target" value={<span className="text-[11px]"><span className="capitalize text-muted-foreground">{event.targetType}:</span> <code>{event.targetId}</code> {event.targetLabel && <span>· {event.targetLabel}</span>}</span>} />}
                {event.ipAddress && <Row label="IP address" value={<code className="text-[11px]">{event.ipAddress}</code>} />}
                {event.userAgent && <Row label="User agent" value={<code className="text-[10px] text-muted-foreground">{event.userAgent}</code>} />}
              </SectionCard>

              {event.classifications.length > 0 && (
                <SectionCard title="Classifications">
                  <div className="flex flex-wrap gap-1.5">
                    {event.classifications.map(c => {
                      const cs = CLASSIFICATION_STYLES[c] ?? { ...FALLBACK_STYLE, label: c };
                      return (
                        <Badge key={c} variant="outline" className={cn('text-[10px]', cs.color)}>
                          {cs.label}
                        </Badge>
                      );
                    })}
                  </div>
                </SectionCard>
              )}

              {event.frameworkControls.length > 0 && (
                <SectionCard title="Framework Controls">
                  <div className="flex flex-wrap gap-1.5">
                    {event.frameworkControls.map(fc => (
                      <code key={fc} className="text-[10px] bg-muted px-2 py-0.5 rounded border">{fc}</code>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Framework controls this event is relevant to. Appears in evidence packs for these frameworks.
                  </p>
                </SectionCard>
              )}

              {(event.externalDestination || event.bytesIn !== null || event.bytesOut !== null) && (
                <SectionCard title="Data Flow">
                  {event.externalDestination && <Row label="Destination" value={<code className="text-[11px]">{event.externalDestination}</code>} />}
                  {event.externalTier && <Row label="Tier" value={<Badge variant="outline" className="text-[10px] capitalize">{event.externalTier.replace(/_/g, ' ')}</Badge>} />}
                  {event.bytesIn !== null && <Row label="Bytes in" value={<span className="text-[11px]">{event.bytesIn.toLocaleString()}</span>} />}
                  {event.bytesOut !== null && <Row label="Bytes out" value={<span className="text-[11px]">{event.bytesOut.toLocaleString()}</span>} />}
                </SectionCard>
              )}
            </TabsContent>

            {/* ─── Session Timeline ─────────────────────────────────────── */}
            <TabsContent value="session" className="m-0 p-6 space-y-4">
              {parentEvent && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Parent event</p>
                  <div className="rounded-md border bg-muted/20 p-3">
                    <TimelineRow
                      e={parentEvent}
                      highlight={false}
                      onSelect={() => onEventChange?.(parentEvent)}
                    />
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Session timeline <span className="normal-case">({sessionEvents.length} events)</span>
                </p>
                <div className="rounded-md border divide-y overflow-hidden">
                  {sessionEvents.map(e => (
                    <TimelineRow
                      key={e.id}
                      e={e}
                      highlight={e.id === event.id}
                      onSelect={() => { if (e.id !== event.id) onEventChange?.(e); }}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  Events grouped by conversation. Each row shows chronological order within the session. Click any row to open its forensic detail.
                </p>
              </div>

              {childEvents.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Direct children <span className="normal-case">({childEvents.length})</span>
                  </p>
                  <div className="rounded-md border divide-y overflow-hidden">
                    {childEvents.map(c => (
                      <TimelineRow
                        key={c.id}
                        e={c}
                        highlight={false}
                        onSelect={() => onEventChange?.(c)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* ─── Payload ──────────────────────────────────────────────── */}
            <TabsContent value="payload" className="m-0 p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold">Input</p>
                  {event.inputSnippet && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => handleCopy(event.inputSnippet!, 'Input')}>
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                  )}
                </div>
                <div className="rounded-md border bg-muted/20 p-3 min-h-[80px]">
                  {event.inputSnippet ? (
                    <pre className="text-[11px] whitespace-pre-wrap font-mono">{event.inputSnippet}</pre>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No input recorded for this event</p>
                  )}
                </div>
                {event.inputSnippet && event.inputSnippet.length >= 200 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Input truncated to 200 chars. Full input in hash-chain audit log.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold">Output</p>
                  {event.outputSnippet && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => handleCopy(event.outputSnippet!, 'Output')}>
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                  )}
                </div>
                <div className="rounded-md border bg-muted/20 p-3 min-h-[80px]">
                  {event.outputSnippet ? (
                    <pre className="text-[11px] whitespace-pre-wrap font-mono">{event.outputSnippet}</pre>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No output recorded for this event</p>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ─── Metadata ─────────────────────────────────────────────── */}
            <TabsContent value="metadata" className="m-0 p-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold">Raw metadata</p>
                <Button
                  size="sm" variant="ghost" className="h-6 text-[10px]"
                  onClick={() => handleCopy(JSON.stringify(event.metadata, null, 2), 'Metadata JSON')}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy
                </Button>
              </div>
              <div className="rounded-md border bg-muted/20 p-3 overflow-x-auto">
                {Object.keys(event.metadata).length > 0 ? (
                  <pre className="text-[11px] font-mono">{JSON.stringify(event.metadata, null, 2)}</pre>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No custom metadata for this event</p>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Platform-emitted structured data. Includes classification reasoning, policy rule IDs, model parameters,
                and other event-type-specific fields.
              </p>
            </TabsContent>

            {/* ─── Hash Chain ───────────────────────────────────────────── */}
            <TabsContent value="chain" className="m-0 p-6 space-y-4">
              <div className="rounded-lg border bg-emerald-500/5 border-emerald-500/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  {verifyResult == null ? (
                    <>
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm font-semibold">Tamper-evident log</p>
                    </>
                  ) : verifyResult.passed ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <p className="text-sm font-semibold">Hash chain verified</p>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400">Chain verification failed</p>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mb-3">
                  {verifyResult == null
                    ? 'This event is part of a tamper-evident, append-only log. Click Verify to recompute the hash chain from this event on the server and confirm no tampering.'
                    : verifyResult.passed
                    ? `Verified ${verifyResult.recordsChecked} record(s) (#${verifyResult.from}–#${verifyResult.to}) on the server. All hashes match — no tampering detected.`
                    : (verifyResult.firstFailureReason ?? `Tampering detected at sequence #${verifyResult.firstFailureAt ?? '?'}.`)}
                </p>
                <Button size="sm" variant="outline" className="gap-1.5 h-7 text-[10px]" onClick={handleVerifyChain} disabled={verifying}>
                  <Shield className="h-3 w-3" /> {verifying ? 'Verifying…' : 'Verify chain from here'}
                </Button>
              </div>

              <SectionCard title="This event">
                <Row label="Sequence #" value={<code className="text-[11px]">{event.sequenceNumber}</code>} />
                <Row label="Timestamp"  value={<code className="text-[11px]">{event.timestamp}</code>} />
                <Row
                  label="Hash"
                  value={
                    <div className="flex items-center gap-1.5">
                      <code className="text-[11px] break-all">{event.hash}</code>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => handleCopy(event.hash, 'Hash')}>
                        <Copy className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  }
                />
                <Row
                  label="Previous hash"
                  value={event.previousHash ? (
                    <div className="flex items-center gap-1.5">
                      <code className="text-[11px] break-all">{event.previousHash}</code>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => handleCopy(event.previousHash!, 'Previous hash')}>
                        <Copy className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  ) : <span className="text-[11px] text-muted-foreground italic">Genesis event (no predecessor)</span>}
                />
              </SectionCard>

              <SectionCard title="Forensic record">
                <p className="text-[11px] text-muted-foreground">
                  Retention: <strong className="text-foreground">5 years</strong> (ITAR), <strong className="text-foreground">6 years</strong> (HIPAA),
                  <strong className="text-foreground"> 3 years</strong> (CMMC), configurable per tenant.
                </p>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  This record is stored in the PostgreSQL audit_logs table with RLS enforcement + hash-chained integrity.
                  Deletion requires platform-admin approval and breaks the chain (flagged at next verification).
                </p>
              </SectionCard>
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer actions */}
        <Separator />
        <div className="p-4 flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={handleExportEvent}>
            <Download className="h-3.5 w-3.5" /> Export Event
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={handleExportSession}>
            <Download className="h-3.5 w-3.5" /> Export Session ({sessionEvents.length})
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-3.5 w-3.5 mr-1" /> Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── SectionCard ────────────────────────────────────────────────────────────

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{title}</p>
      <div className="rounded-md border bg-card p-3 space-y-2">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div>{value}</div>
    </div>
  );
}

// ─── TimelineRow ────────────────────────────────────────────────────────────

function TimelineRow({ e, highlight, onSelect }: { e: ActivityEvent; highlight: boolean; onSelect: () => void }) {
  const cat = CATEGORY_STYLES[e.category] ?? { ...FALLBACK_STYLE, label: e.category };
  const oc = OUTCOME_STYLES[e.outcome] ?? { ...FALLBACK_STYLE, label: e.outcome };
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-3 py-2 text-xs hover:bg-muted/30 cursor-pointer',
        highlight && 'bg-primary/5',
      )}
      onClick={onSelect}
    >
      <span className="font-mono text-[9px] text-muted-foreground shrink-0 mt-0.5">#{e.sequenceNumber}</span>
      <div className="flex flex-col gap-0.5 shrink-0 w-16">
        <Badge variant="outline" className={cn('text-[8px] h-3.5 px-1 w-fit', cat.color)}>{cat.label}</Badge>
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('truncate', highlight && 'font-semibold')}>{e.summary}</p>
        <p className="text-[9px] text-muted-foreground mt-0.5">{relativeTime(e.timestamp)}</p>
      </div>
      <Badge variant="outline" className={cn('text-[8px] h-3.5 px-1 shrink-0 mt-0.5', oc.color)}>{oc.label}</Badge>
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
